import { FormattedMessage } from "react-intl";
import { AlertBanner, Text } from "@lendcord/concord";
import type { RoleUpgradeRequestStatus } from "@/app/profile/profile.types";

interface UpgradeRequestStatusBannerProps {
  status: RoleUpgradeRequestStatus | undefined;
  remarks?: string;
}

// Renders the post-request state for a LENDER who has already submitted (or
// had decided) a role-upgrade request. The `allowRequestForUpgradation`
// flag (checked by the parent via GET /profile) is the gate — only when false
// do we land here, and `status` tells us why.
export function UpgradeRequestStatusBanner({ status, remarks }: UpgradeRequestStatusBannerProps) {
  switch (status) {
    case "APPROVED":
      return (
        <AlertBanner status="success" className="mt-4">
          <FormattedMessage id="lender.profile.upgradeToCreditorApproved" />
        </AlertBanner>
      );
    case "REJECTED":
      return (
        <AlertBanner status="warning" className="mt-4">
          <FormattedMessage
            id="lender.profile.upgradeToCreditorRejected"
            values={{
              remarks: remarks ?? <FormattedMessage id="lender.profile.upgradeToCreditorRejectedNoRemarks" />,
            }}
          />
        </AlertBanner>
      );
    case "IN_PROGRESS":
      return (
        <AlertBanner status="info" className="mt-4">
          <FormattedMessage id="lender.profile.upgradeToCreditorInProgress" />
          {remarks && (
            <Text as="p" size="xs" variant="muted" className="mt-1">
              {remarks}
            </Text>
          )}
        </AlertBanner>
      );
    case "NOT_APPLIED":
    default:
      // Reachable when an admin upgrades a LENDER directly (the "plain
      // upgrade" path, no self-serve request row ever created) — the caller
      // is no longer eligible to request (their role already changed
      // server-side) but there's no request history to describe, so there's
      // nothing accurate to say here. Falling through to the "in progress"
      // copy would be actively misleading (no request was ever made, and
      // they may already be upgraded pending their next login).
      return null;
  }
}
